import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';

class SearchableSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      open: false,
    };
  }

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value });
  };

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false, searchText: '' });
  };

  filterItems = (items, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return items;
    }
    const lowerSearchText = searchText.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(lowerSearchText)
    );
  };

  highlightText = (text, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return <span>{text}</span>;
    }
    
    const lowerText = text.toLowerCase();
    const lowerSearchText = searchText.toLowerCase();
    const startIndex = lowerText.indexOf(lowerSearchText);
    
    if (startIndex === -1) {
      return <span>{text}</span>;
    }
    
    const endIndex = startIndex + searchText.length;
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, endIndex);
    const afterMatch = text.substring(endIndex);
    
    return (
      <span>
        {beforeMatch}
        <span style={{ 
          backgroundColor: '#e0e0e0',
          fontWeight: 'bold'
        }}>
          {match}
        </span>
        {afterMatch}
      </span>
    );
  };

  render() {
    const {
      label,
      items = [],
      value = '',
      onChange,
      placeholder = "Search...",
      id,
      disabled = false,
      fullWidth = true,
      className,
      sx,
      variant = "standard",
      showLabel = true,
      menuPlacement = "bottom",
    } = this.props;

    const { searchText, open } = this.state;
    const filteredItems = this.filterItems(items, searchText);
    
    // Only use value if it exists in items array, otherwise use empty string
    const safeValue = items.some(item => item.id === value) ? value : '';

    return (
      <FormControl 
        sx={{ width: fullWidth ? '100%' : 'auto', ...sx }} 
        className={className}
        disabled={disabled}
        variant={variant}
      >
        {showLabel && (
          <InputLabel htmlFor={id} shrink>
            {label}
          </InputLabel>
        )}
        <Select
          open={open}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          value={safeValue}
          onChange={onChange}
          inputProps={{
            name: id,
            id: id,
          }}
          variant={variant}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 280,
              },
            },
            autoFocus: false,
            disableAutoFocusItem: true,
            anchorOrigin: {
              vertical: menuPlacement === 'top' ? 'top' : 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: menuPlacement === 'top' ? 'bottom' : 'top',
              horizontal: 'left',
            },
            marginThreshold: 8,
          }}
        >
          <MenuItem 
            disableRipple
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0',
              padding: '8px 16px',
              cursor: 'default'
            }}
          >
            <TextField
              size="small"
              placeholder={placeholder}
              fullWidth
              value={searchText}
              onChange={this.handleSearchChange}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span role="img" aria-label="search" style={{ color: '#757575' }}>🔍</span>
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <span 
                      role="button"
                      tabIndex={0}
                      onClick={() => this.setState({ searchText: '' })}
                      style={{
                        cursor: 'pointer',
                        color: '#757575',
                        fontSize: '18px',
                        lineHeight: 1,
                        padding: '0 4px'
                      }}
                    >
                      ×
                    </span>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  }
                }
              }}
            />
          </MenuItem>
          {filteredItems.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {this.highlightText(item.name, searchText)}
            </MenuItem>
          ))}
          {filteredItems.length === 0 && (
            <MenuItem disabled style={{ justifyContent: 'center', color: '#757575' }}>
              No {label.toLowerCase()}s found
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  }
}

SearchableSelect.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
  variant: PropTypes.string,
  showLabel: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['top', 'bottom']),
};

export default SearchableSelect;



// WEBPACK FOOTER //
// ./src/components/common/SearchableSelect.js